import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldCheck, LogOut } from "lucide-react";

type CompanyRow = {
  id: string;
  name: string;
  code: string;
  contact_email: string | null;
  contact_phone: string | null;
  is_demo: boolean | null;
  demo_expires_at: string | null;
  created_at: string;
};

type LeadRow = {
  id: string;
  email: string | null;
  company_name: string | null;
  wants_contact: boolean | null;
  company_id: string | null;
  created_at: string;
};

type ProfileRow = {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  company_id: string | null;
  crew: string | null;
  enrolled: boolean | null;
  is_active: boolean | null;
  created_at: string;
};

type RoleRow = { user_id: string; role: string };

type CustomerRow = {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  zip: string | null;
  company_id: string | null;
  frequency: string | null;
  service_day: string | null;
  price: number | null;
  source: string | null;
  is_shared_demo: boolean | null;
};

type Counts = Record<string, number>;

const fmtDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

const matches = (q: string, ...fields: (string | null | undefined)[]) =>
  !q || fields.some((f) => (f ?? "").toLowerCase().includes(q));

export default function Admin() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [jobCounts, setJobCounts] = useState<Counts>({});
  const [crewCount, setCrewCount] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth", { replace: true });
      return;
    }
    let active = true;
    supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }).then(({ data }) => {
      if (!active) return;
      setIsAdmin(Boolean(data));
      setChecking(false);
    });
    return () => {
      active = false;
    };
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    (async () => {
      setLoading(true);
      const [companiesRes, leadsRes, profilesRes, rolesRes, customersRes, jobsRes, crewsRes] = await Promise.all([
        supabase.from("companies").select("*").order("created_at", { ascending: false }),
        supabase.from("demo_leads").select("*").order("created_at", { ascending: false }),
        supabase
          .from("profiles")
          .select("id,full_name,email,phone,company_id,crew,enrolled,is_active,created_at")
          .order("created_at", { ascending: false }),
        supabase.from("user_roles").select("user_id,role"),
        supabase
          .from("customers")
          .select("id,name,address,city,zip,company_id,frequency,service_day,price,source,is_shared_demo")
          .order("name"),
        supabase.from("jobs").select("company_id"),
        supabase.from("crews").select("id", { count: "exact", head: true }),
      ]);
      if (!active) return;
      setCompanies((companiesRes.data as CompanyRow[]) ?? []);
      setLeads((leadsRes.data as LeadRow[]) ?? []);
      setProfiles((profilesRes.data as ProfileRow[]) ?? []);
      setRoles((rolesRes.data as RoleRow[]) ?? []);
      setCustomers((customersRes.data as CustomerRow[]) ?? []);
      const jc: Counts = {};
      for (const row of jobsRes.data ?? []) {
        const key = (row as { company_id: string | null }).company_id ?? "none";
        jc[key] = (jc[key] ?? 0) + 1;
      }
      setJobCounts(jc);
      setCrewCount(crewsRes.count ?? 0);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [isAdmin]);

  const companyById = useMemo(() => {
    const map: Record<string, CompanyRow> = {};
    for (const c of companies) map[c.id] = c;
    return map;
  }, [companies]);

  const roleByUser = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const r of roles) (map[r.user_id] ??= []).push(r.role);
    return map;
  }, [roles]);

  const customerCounts = useMemo(() => {
    const cc: Counts = {};
    for (const c of customers) {
      const key = c.company_id ?? "none";
      cc[key] = (cc[key] ?? 0) + 1;
    }
    return cc;
  }, [customers]);

  const realCompanies = useMemo(() => companies.filter((c) => !c.is_demo), [companies]);
  const demoCompanies = useMemo(() => companies.filter((c) => c.is_demo), [companies]);
  const totalJobs = useMemo(() => Object.values(jobCounts).reduce((a, b) => a + b, 0), [jobCounts]);
  const hotLeads = useMemo(() => leads.filter((l) => l.wants_contact).length, [leads]);
  const crewMembers = useMemo(() => profiles.filter((p) => (roleByUser[p.id] ?? []).includes("mower")), [profiles, roleByUser]);

  const q = search.trim().toLowerCase();
  const companyName = (id: string | null) => (id ? companyById[id]?.name ?? "—" : "—");

  const filteredCompanies = (rows: CompanyRow[]) =>
    rows.filter((c) => matches(q, c.name, c.code, c.contact_email, c.contact_phone));
  const filteredProfiles = (rows: ProfileRow[]) =>
    rows.filter((p) => matches(q, p.full_name, p.email, p.phone, companyName(p.company_id)));
  const filteredCustomers = useMemo(
    () => customers.filter((c) => matches(q, c.name, c.address, c.city, c.zip, companyName(c.company_id))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customers, q, companyById],
  );
  const filteredLeads = leads.filter((l) => matches(q, l.email, l.company_name));

  if (authLoading || checking) {
    return (
      <main className="mx-auto max-w-5xl space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Not authorized</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>This area is restricted to Mowzi platform administrators.</p>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Back to dashboard
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  const companyTable = (rows: CompanyRow[]) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Company</TableHead>
            <TableHead>Code</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead className="text-right">Customers</TableHead>
            <TableHead className="text-right">Jobs</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Expires</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="py-6 text-center text-muted-foreground">
                Nothing here yet
              </TableCell>
            </TableRow>
          )}
          {rows.map((c) => (
            <TableRow key={c.id}>
              <TableCell className="font-medium">{c.name}</TableCell>
              <TableCell>
                <Badge variant="secondary">{c.code}</Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {c.contact_email || c.contact_phone || "—"}
              </TableCell>
              <TableCell className="text-right">{customerCounts[c.id] ?? 0}</TableCell>
              <TableCell className="text-right">{jobCounts[c.id] ?? 0}</TableCell>
              <TableCell className="text-sm">{fmtDate(c.created_at)}</TableCell>
              <TableCell className="text-sm">{c.is_demo ? fmtDate(c.demo_expires_at) : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const peopleTable = (rows: ProfileRow[], showCrew: boolean) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Role</TableHead>
            {showCrew && <TableHead>Crew</TableHead>}
            <TableHead>Status</TableHead>
            <TableHead>Joined</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={showCrew ? 7 : 6} className="py-6 text-center text-muted-foreground">
                No users found
              </TableCell>
            </TableRow>
          )}
          {rows.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="font-medium">{p.full_name || "—"}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{p.email || p.phone || "—"}</TableCell>
              <TableCell className="text-sm">{companyName(p.company_id)}</TableCell>
              <TableCell className="text-sm capitalize">{(roleByUser[p.id] ?? []).join(", ") || "—"}</TableCell>
              {showCrew && <TableCell className="text-sm">{p.crew || "N/A"}</TableCell>}
              <TableCell>
                {p.enrolled ? <Badge variant="secondary">Enrolled</Badge> : <Badge variant="outline">Invited</Badge>}
              </TableCell>
              <TableCell className="text-sm">{fmtDate(p.created_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <main className="mx-auto max-w-7xl space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-xl font-semibold md:text-2xl">Mowzi admin</h1>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")}>
            Company view
          </Button>
          <Button variant="ghost" size="sm" onClick={signOut}>
            <LogOut className="mr-1 h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-3 md:grid-cols-6">
        {[
          { label: "Companies", value: realCompanies.length },
          { label: "Demo sandboxes", value: demoCompanies.length },
          { label: "Users", value: profiles.length },
          { label: "Crew members", value: crewMembers.length },
          { label: "Customers", value: customers.length },
          { label: "Jobs", value: totalJobs },
        ].map((kpi) => (
          <Card key={kpi.label}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{kpi.label}</p>
              <p className="text-2xl font-semibold">{loading ? "—" : kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </section>

      <Input
        placeholder="Search companies, users, customers or leads"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
      />

      <Tabs defaultValue="companies">
        <TabsList className="flex-wrap">
          <TabsTrigger value="companies">Companies ({realCompanies.length})</TabsTrigger>
          <TabsTrigger value="demos">Demos ({demoCompanies.length})</TabsTrigger>
          <TabsTrigger value="users">Users ({profiles.length})</TabsTrigger>
          <TabsTrigger value="crew">Crew ({crewMembers.length})</TabsTrigger>
          <TabsTrigger value="customers">Customers ({customers.length})</TabsTrigger>
          <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="companies">
          <Card>
            <CardContent className="p-0">
              {loading ? <Skeleton className="m-4 h-32" /> : companyTable(filteredCompanies(realCompanies))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="demos">
          <Card>
            <CardContent className="p-0">
              {loading ? <Skeleton className="m-4 h-32" /> : companyTable(filteredCompanies(demoCompanies))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card>
            <CardContent className="p-0">
              {loading ? <Skeleton className="m-4 h-32" /> : peopleTable(filteredProfiles(profiles), false)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="crew">
          <Card>
            <CardContent className="p-0">
              {loading ? <Skeleton className="m-4 h-32" /> : peopleTable(filteredProfiles(crewMembers), true)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customers">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <Skeleton className="m-4 h-32" />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Frequency</TableHead>
                        <TableHead>Day</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead>Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCustomers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="py-6 text-center text-muted-foreground">
                            No customers found
                          </TableCell>
                        </TableRow>
                      )}
                      {filteredCustomers.slice(0, 500).map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {[c.address, c.city, c.zip].filter(Boolean).join(", ") || "—"}
                          </TableCell>
                          <TableCell className="text-sm">{companyName(c.company_id)}</TableCell>
                          <TableCell className="text-sm capitalize">{c.frequency || "—"}</TableCell>
                          <TableCell className="text-sm">{c.service_day || "—"}</TableCell>
                          <TableCell className="text-right text-sm">
                            {c.price != null ? `$${Number(c.price).toFixed(0)}` : "—"}
                          </TableCell>
                          <TableCell>
                            <Badge variant={c.source === "Mowzi" ? "default" : "secondary"}>{c.source || "Legacy"}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {filteredCustomers.length > 500 && (
                    <p className="p-3 text-xs text-muted-foreground">
                      Showing first 500 of {filteredCustomers.length} customers — refine your search.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leads">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Sandbox</TableHead>
                      <TableHead>Wants contact</TableHead>
                      <TableHead>Submitted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="py-6 text-center text-muted-foreground">
                          No demo leads yet
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredLeads.map((l) => (
                      <TableRow key={l.id}>
                        <TableCell className="font-medium">{l.email || "—"}</TableCell>
                        <TableCell>{l.company_name || "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{companyName(l.company_id)}</TableCell>
                        <TableCell>
                          {l.wants_contact ? <Badge>Yes</Badge> : <span className="text-muted-foreground">No</span>}
                        </TableCell>
                        <TableCell className="text-sm">{fmtDate(l.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="border-t p-3 text-xs text-muted-foreground">
                {hotLeads} of {leads.length} leads asked to be contacted about a real account.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  );
}
